/**
 * Select Role Screen
 *
 * Shown when a user authenticates but has role='pending' (or no profile).
 * Displays LinkedIn info if available and two role cards.
 * On selection: upserts profile, sets stores, routes to onboarding.
 */

import React, { useState } from 'react';
import { View, Text, Image, ActivityIndicator } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Rocket, TrendingUp, ArrowRight } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useAuth, supabase } from '@/lib/supabase';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { reportError } from '@/lib/errors';
import { AuthLayout } from '@/components/AuthLayout';
import { isWeb } from '@/lib/platform';

import { LOGO_MARK } from '@/lib/logo';

export default function SelectRoleScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, refreshProfile } = useAuth();
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);
  const initializeOnboarding = useProfileStore((s) => s.initializeOnboarding);

  const [selectedRole, setSelectedRole] = useState<'startup' | 'investor' | null>(null);

  // Extract user display info from LinkedIn metadata
  const meta = user?.user_metadata || {};
  const linkedInData = meta.linkedin_data as Record<string, string> | null;
  const userName = linkedInData?.name || meta.full_name || meta.name || user?.email?.split('@')[0] || '';
  const userAvatar = linkedInData?.avatarUrl || meta.avatar_url || meta.picture || null;

  const selectRoleMutation = useMutation({
    mutationFn: async (role: 'startup' | 'investor') => {
      setUserRole(role);
      setSubscriptionUserRole(role);
      initializeOnboarding(role);

      if (user) {
        // @ts-ignore - Supabase types mismatch
        const { error: roleErr } = await supabase.from('profiles').upsert({
          id: user.id,
          email: user.email!,
          role,
          onboarding_completed: false,
          ...(linkedInData ? { onboarding_progress: { linkedin_data: linkedInData } } : {}),
        }, { onConflict: 'id' });

        if (roleErr) {
          reportError(roleErr, { context: 'SelectRole.upsertProfile', silent: true });
        }

        // No placeholder company here. This used to upsert a "My Startup"
        // startup_profiles row keyed on user_id — ownership by a column that is
        // retired and write-NULL-only since 118-10 (a value let erasing the
        // founder delete the company). The onboarding company step creates or
        // claims the company, and ownership is team membership.

        refreshProfile().catch((err) =>
          reportError(err, { context: 'SelectRole.refreshProfile', silent: true })
        );
        // NOTE: No supabase.auth.updateUser() call here — updateUser acquires
        // the GoTrue navigator lock which blocks all subsequent DB operations.
        // Role is authoritative in profiles table.
      }

      return role;
    },
    onSuccess: (role) => {
      router.replace(`/(onboarding)/${role}-welcome` as any);
    },
  });

  const isLoading = selectRoleMutation.isPending;

  const handleSelectRole = (role: 'startup' | 'investor') => {
    if (isLoading) return;
    setSelectedRole(role);
    selectRoleMutation.mutate(role);
  };

  const content = (
    <View className="flex-1 justify-center" style={{ paddingHorizontal: isWeb ? 40 : 24, paddingVertical: 32 }}>
      {/* Step indicator */}
      <Animated.View
        entering={FadeInDown.delay(50).duration(400)}
        className="items-center mb-6"
      >
        <View style={{
          backgroundColor: colors.primary[50],
          paddingHorizontal: 14,
          paddingVertical: 6,
          borderRadius: 20,
        }}>
          <Text style={{ color: colors.primary[600], fontSize: 12, fontWeight: '600', letterSpacing: 0.5 }}>
            STEP 1 OF 3 — ACCOUNT SETUP
          </Text>
        </View>
      </Animated.View>

      {/* User greeting */}
      <Animated.View
        entering={FadeInDown.delay(100).duration(500)}
        className="items-center mb-10"
      >
        {userAvatar ? (
          <View style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            marginBottom: 16,
            borderWidth: 2.5,
            borderColor: colors.primary[500],
            overflow: 'hidden',
          }}>
            <Image
              source={{ uri: userAvatar }}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
            />
          </View>
        ) : (
          <Image
            source={LOGO_MARK}
            style={{ width: 56, height: 56, marginBottom: 16 }}
            resizeMode="contain"
          />
        )}
        {userName ? (
          <>
            <Text
              className="text-2xl font-bold text-center mb-2"
              style={{ color: colors.text.primary }}
            >
              Welcome, {userName.split(' ')[0]}
            </Text>
            <Text
              className="text-base text-center"
              style={{ color: colors.text.secondary, maxWidth: 300 }}
            >
              How will you use Inverio?
            </Text>
          </>
        ) : (
          <>
            <Text
              className="text-2xl font-bold text-center mb-2"
              style={{ color: colors.text.primary }}
            >
              Choose Your Role
            </Text>
            <Text
              className="text-base text-center"
              style={{ color: colors.text.secondary, maxWidth: 300 }}
            >
              Select how you'll use Inverio to get started.
            </Text>
          </>
        )}
      </Animated.View>

      {/* Role Cards */}
      <Animated.View
        entering={FadeInUp.delay(250).duration(500)}
        className="gap-4"
        style={{ maxWidth: isWeb ? 400 : undefined, width: '100%', alignSelf: 'center' }}
      >
        {/* Startup Card */}
        <AnimatedPressable
          onPress={() => handleSelectRole('startup')}
          disabled={isLoading}
          accessibilityRole="button"
          accessibilityLabel="Continue as a startup founder"
        >
          <View
            style={{
              backgroundColor: selectedRole === 'startup' ? colors.primary[50] : colors.surface.primary,
              borderWidth: 1.5,
              borderColor: selectedRole === 'startup' ? colors.primary[500] : colors.border.light,
              borderRadius: 16,
              padding: 20,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: selectedRole === 'startup' ? colors.primary[100] : colors.surface.secondary,
              }}
            >
              {isLoading && selectedRole === 'startup' ? (
                <ActivityIndicator size="small" color={colors.primary[600]} />
              ) : (
                <Rocket size={22} color={selectedRole === 'startup' ? colors.primary[600] : colors.text.tertiary} />
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  color: selectedRole === 'startup' ? colors.primary[700] : colors.text.primary,
                  fontSize: 16,
                  fontWeight: '700',
                  marginBottom: 3,
                }}
              >
                Startup Founder
              </Text>
              <Text style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18 }}>
                Get scored, manage your data room, and connect with investors.
              </Text>
            </View>
            <ArrowRight
              size={18}
              color={selectedRole === 'startup' ? colors.primary[600] : colors.border.dark}
            />
          </View>
        </AnimatedPressable>

        {/* Investor Card */}
        <AnimatedPressable
          onPress={() => handleSelectRole('investor')}
          disabled={isLoading}
          accessibilityRole="button"
          accessibilityLabel="Continue as an investor"
        >
          <View
            style={{
              backgroundColor: selectedRole === 'investor' ? colors.primary[50] : colors.surface.primary,
              borderWidth: 1.5,
              borderColor: selectedRole === 'investor' ? colors.primary[500] : colors.border.light,
              borderRadius: 16,
              padding: 20,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: selectedRole === 'investor' ? colors.primary[100] : colors.surface.secondary,
              }}
            >
              {isLoading && selectedRole === 'investor' ? (
                <ActivityIndicator size="small" color={colors.primary[600]} />
              ) : (
                <TrendingUp size={22} color={selectedRole === 'investor' ? colors.primary[600] : colors.text.tertiary} />
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  color: selectedRole === 'investor' ? colors.primary[700] : colors.text.primary,
                  fontSize: 16,
                  fontWeight: '700',
                  marginBottom: 3,
                }}
              >
                Angel Investor
              </Text>
              <Text style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18 }}>
                Discover startups, run due diligence, and manage your dealflow.
              </Text>
            </View>
            <ArrowRight
              size={18}
              color={selectedRole === 'investor' ? colors.primary[600] : colors.border.dark}
            />
          </View>
        </AnimatedPressable>
      </Animated.View>

      {/* Footer hint */}
      <Animated.View
        entering={FadeInUp.delay(400).duration(500)}
        className="items-center mt-8"
      >
        <Text style={{ color: colors.text.tertiary, fontSize: 12, textAlign: 'center' }}>
          You can always change your role later in settings.
        </Text>
      </Animated.View>
    </View>
  );

  // Mobile: SafeAreaView wrapping. Web: AuthLayout handles responsive layout.
  if (isWeb) {
    return (
      <AuthLayout>
        {content}
      </AuthLayout>
    );
  }

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView className="flex-1">
        {content}
      </SafeAreaView>
    </View>
  );
}
